import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import {
  deleteWatchBrand,
  previewWatchBrandDelete,
  type WatchBrandDeletePreview,
} from '../../services/firebase';
import type { WatchBrand } from '../../types';

interface BrandDeleteModalProps {
  /** The brand to delete; `null` keeps the modal closed. */
  brand: WatchBrand | null;
  onClose: () => void;
  /** Called once the brand is gone, so the host can drop its row. */
  onDeleted: (brandId: string) => void;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

function errorMessage(error: unknown, fallback: string): string {
  const message = error instanceof Error ? error.message : '';
  return message && message !== 'internal' ? message : fallback;
}

/**
 * One line per thing the delete removes or rewrites, in the order a curator
 * would care about: the brand itself, its files, then other records.
 *
 * Exported for the test: the modal's promise is that this list is EXACTLY what
 * the server does, so every preview field that causes a write has a line here.
 */
export function brandDeleteLines(preview: WatchBrandDeletePreview): { text: string; detail?: string }[] {
  const lines: { text: string; detail?: string }[] = [
    { text: 'The brand record, and its entry in the search index' },
  ];
  if (preview.storagePaths.length > 0) {
    lines.push({
      text: `${plural(preview.storagePaths.length, 'image file')} from Storage`,
      detail: 'Logo, hero, glyph, timeline and history images',
    });
  }
  if (preview.importSource) {
    lines.push({ text: 'Its import source settings and import run history' });
  }
  if (preview.subcollections.length > 0) {
    lines.push({
      text: `${plural(preview.subcollections.length, 'sub-collection')} stored under the brand`,
      detail: preview.subcollections.join(', '),
    });
  }
  if (preview.calibreOwners.length > 0) {
    lines.push({
      text: `Brand link cleared on ${plural(preview.calibreOwners.length, 'calibre')} (the calibres are kept)`,
      detail: preview.calibreOwners.map((c) => c.name).join(', '),
    });
  }
  if (preview.calibreAliases.length > 0) {
    const aliasCount = preview.calibreAliases.reduce((n, c) => n + c.removed.length, 0);
    lines.push({
      text: `${plural(aliasCount, 'calibre alias', 'calibre aliases')} scoped to this brand`,
      detail: preview.calibreAliases.map((c) => `${c.name}: ${c.removed.join(', ')}`).join('; '),
    });
  }
  if (preview.brandAcquisitions.length > 0) {
    const entries = preview.brandAcquisitions.reduce((n, b) => n + b.removed, 0);
    lines.push({
      text: `${plural(entries, 'acquisition entry', 'acquisition entries')} on other brands`,
      detail: preview.brandAcquisitions.map((b) => b.name).join(', '),
    });
  }
  if (preview.manufacturers.length > 0) {
    lines.push({
      text: `Its link to ${plural(preview.manufacturers.length, 'movement manufacturer')}`,
      detail: preview.manufacturers.map((m) => m.name).join(', '),
    });
  }
  if (preview.parentOrgs.length > 0) {
    const events = preview.parentOrgs.reduce((n, o) => n + o.removed, 0);
    lines.push({
      text: `${plural(events, 'ownership event')} on parent organizations`,
      detail: preview.parentOrgs.map((o) => o.name).join(', '),
    });
  }
  if (preview.users.length > 0) {
    lines.push({
      text: `Brand Portal access for ${plural(preview.users.length, 'user')}`,
      detail: preview.users.map((u) => u.email).join(', '),
    });
  }
  if (preview.inviteCount > 0) {
    lines.push({ text: plural(preview.inviteCount, 'Brand Portal invite') });
  }
  return lines;
}

/**
 * Confirmation for deleting a brand. Opening it asks the server what the delete
 * would remove; the list shown is that answer, and confirming deletes exactly
 * that list or fails.
 */
export function BrandDeleteModal({ brand, onClose, onDeleted }: BrandDeleteModalProps) {
  const [preview, setPreview] = useState<WatchBrandDeletePreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  /** Bumped to re-run the preview after the server reports it went stale. */
  const [attempt, setAttempt] = useState(0);
  const brandId = brand?.id;

  useEffect(() => {
    setPreview(null);
    setLoadError(null);
    if (!brandId) return;
    let cancelled = false;
    previewWatchBrandDelete(brandId)
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch((error: unknown) => {
        console.error('Failed to preview brand delete:', error);
        if (!cancelled) setLoadError(errorMessage(error, 'Could not check what deleting this brand would remove.'));
      });
    return () => {
      cancelled = true;
    };
  }, [brandId, attempt]);

  if (!brand) return null;

  const handleDelete = async () => {
    if (!preview) return;
    setIsDeleting(true);
    try {
      const result = await deleteWatchBrand(brand.id, preview.fingerprint);
      if (result.warnings.length > 0) {
        toast(`Deleted "${brand.name}", with leftovers: ${result.warnings.join(' ')}`, {
          icon: '⚠️',
          duration: 10000,
        });
      } else {
        toast.success(`Deleted "${brand.name}"`);
      }
      onDeleted(brand.id);
      onClose();
    } catch (error) {
      console.error('Error deleting brand:', error);
      toast.error(errorMessage(error, 'Failed to delete brand'));
      // Whatever went wrong, the list on screen can no longer be trusted.
      setAttempt((n) => n + 1);
    } finally {
      setIsDeleting(false);
    }
  };

  const blocked = !!preview && preview.blockers.length > 0;

  return (
    <Modal
      isOpen
      onClose={isDeleting ? () => undefined : onClose}
      title={`Delete "${brand.name}"`}
      size='md'
      dismissOnBackdrop={!isDeleting}
    >
      <div className='space-y-4'>
        {loadError && <p className='text-sm text-red-600'>{loadError}</p>}

        {!preview && !loadError && (
          <p className='text-sm text-gray-500'>Checking what this would remove…</p>
        )}

        {preview && blocked && (
          <div className='space-y-2'>
            <p className='text-sm font-medium text-gray-900'>This brand cannot be deleted yet.</p>
            <ul className='list-disc pl-5 space-y-1 text-sm text-gray-700'>
              {preview.blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
            {preview.blockingTiers.length > 0 && (
              <p className='text-xs text-gray-500'>
                {preview.blockingTiers.map((t) => `${t.calibreName} — ${t.tierName}`).join('; ')}
              </p>
            )}
          </div>
        )}

        {preview && !blocked && (
          <>
            <p className='text-sm text-gray-700'>This permanently removes:</p>
            <ul className='list-disc pl-5 space-y-2 text-sm text-gray-900'>
              {brandDeleteLines(preview).map((line) => (
                <li key={line.text}>
                  {line.text}
                  {line.detail && (
                    <span className='block text-xs text-gray-500 break-words'>{line.detail}</span>
                  )}
                </li>
              ))}
            </ul>
            {preview.referenceImagesKept && (
              <p className='text-xs text-gray-500'>
                Reference images stored under this brand are kept — references moved to another
                brand may still use them.
              </p>
            )}
            <p className='text-sm font-medium text-red-600'>This cannot be undone.</p>
          </>
        )}

        <div className='flex justify-end gap-2 pt-2'>
          <Button variant='secondary' onClick={onClose} disabled={isDeleting}>
            {blocked ? 'Close' : 'Cancel'}
          </Button>
          {!blocked && (
            <Button variant='danger' onClick={handleDelete} disabled={!preview} loading={isDeleting}>
              Delete brand
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
